import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Card, Table, Descriptions, Space, Tabs, Tag, Typography, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import { styled } from 'styled-components';
import moment from 'moment';
import _ from 'lodash';

import { StyleProfileContent, StyleProfileCover } from '../Common/ProfileStyle';
import usecustomStyles from '../Common/customStyles';
import '../assets/scss/_custom.scss';
import * as VThunk from '../slices/vehicles/thunk';
import VehicleLiveViewer from './VehicleLiveViewer';
import { ReactComponent as SvgTruck } from '../assets/images/svgs/truck-tow.svg';
import { coordInRegion } from '../Libs/echarts';

const { Title } = Typography;

const customStyles = usecustomStyles();

const BgPrimary = styled.div`
    background-color: ${({ theme }) => theme.token.colorPrimaryBg};
`;

const VehicleProfile = () => {
    document.title = process.env.REACT_APP_PAGE_TITLE;

    const vehicleLogList = useSelector(state => state.Vehicles.logList);
    const vehicle = useSelector(state => state.Vehicles.vehicle);
    const lastHeartBeat = useSelector(state => state.Vehicles.lastHeartBeat);
    const [lastLocation, setLastLocation] = useState('');
    const [lastOnline, setLastOnline] = useState('');
    const [mapUrl, setMapUrl] = useState('');
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const handleChange = (pagination, filters, sorter) => {
        setFilteredInfo(filters);
        setSortedInfo(sorter);
    };

    const allLogs = () => {
        dispatch(VThunk.getLogs(vehicleId));
    };

    const { vehicleId } = useParams();

    const AlarmEventColumns = [
        {
            title: '日志类型',
            width: 100,
            align: 'center',
            render: item => {
                if (item.op === 'A') {
                    return <Tag color={customStyles.colorDanger}>警报</Tag>;
                }else if (item.op === 'O') {
                    return <Tag color={customStyles.colorSuccess}>倾覆</Tag>;
                }else if (item.op === 'S') {
                    return <Tag color={customStyles.colorSuccess}>状态</Tag>;
                }else {
                    return <Tag color={customStyles.controlHeight}>心跳</Tag>;
                }
                
            }
        },
        {
            title: '时间',
            width: 240,
            align: 'center',
            render: item => moment(item.timestamp).format('YYYY-MM-DD HH:mm:ss')
        },
        {
            title: '日志内容',
            dataIndex: 'data',
            key: 'data',
            align: 'center',
            filters: [
                {
                    text: '离开监督范围',
                    value: '离开监督范围',
                },
                {
                    text: '进入危险区域',
                    value: '进入危险区域',
                },
            ],
            filteredValue: filteredInfo.data || null,
            onFilter: (value, record) => record.data.includes(value),
            // sorter: (a, b) => a.data.length - b.data.length,
            sortOrder: sortedInfo.columnKey === 'data' ? sortedInfo.order : null,
            ellipsis: true,
        }
    ];

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(VThunk.getById(vehicleId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!_.isEmpty(vehicleId)) {
            dispatch(VThunk.getLogsPartial(vehicleId));
            dispatch(VThunk.getLastHeartBeat(vehicleId));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicle]);

    useEffect(() => {
        const rmc = (lastHeartBeat || {}).rmc;
        const name = vehicle.licensePlate;
        if (_.isEmpty(rmc) || _.isEmpty(name)) {
            return;
        }
        const region = coordInRegion(rmc.longitude, rmc.latitude);
        setLastLocation(region ? region.name : '');
        setLastOnline(`${rmc.datetime}`);

        const position = [rmc.longitude, rmc.latitude].join(',');
        setMapUrl(`https://uri.amap.com/marker?position=${position}&name=${name}&coordinate=gaode&callnative=0`);
    }, [lastHeartBeat, vehicle]);

    const sDisplayFlexCenter = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const sAvatarICON = {
        padding: '6px',
        width: '90px',
        height: '90px',
        borderRadius: '4px'
    };

    function getYs7LiveAddr() {
        const deviceSerial = vehicle?.deviceSerial;
        if (!_.isEmpty(deviceSerial)) {
            dispatch(VThunk.getYs7LiveAddr(deviceSerial));
        }
    }

    function onTabChange(key) {
        key = String(key);
        if (key === '2') {
            getYs7LiveAddr();
        }
    }

    const ProfileCard = (
        <Card style={{ minWidth: '30em' }}>
            <Descriptions title="详细信息" column={1} bordered labelStyle={{ textAlign: 'right' }}>
                <Descriptions.Item label="车牌号">{vehicle.licensePlate || '---'}</Descriptions.Item>
                <Descriptions.Item label="所属项目">{vehicle.projectName || '---'}</Descriptions.Item>
                <Descriptions.Item label="所属工地">{vehicle.siteName || '---'}</Descriptions.Item>
                <Descriptions.Item label="工地负责人">{vehicle.siteOwnerName || '---'}</Descriptions.Item>
                <Descriptions.Item label="品牌">{vehicle.brand || '---'}</Descriptions.Item>
                <Descriptions.Item label="类型">{vehicle.type || '---'}</Descriptions.Item>
                <Descriptions.Item label="最大起重重量">{vehicle.maxLoad || '---'}</Descriptions.Item>
                <Descriptions.Item label="进场时间">{vehicle.entryDate || '---'}</Descriptions.Item>
                <Descriptions.Item label="人员信息">
                    {((vehicle || {}).workers || [])
                        .map(x => `${x.role}: ${x.name}(${x.phone || '---'})`)
                        .map(x => (
                            <React.Fragment key={x}>
                                {x} <br />
                            </React.Fragment>
                        ))}
                </Descriptions.Item>
            </Descriptions>
        </Card>
    );
    const pagination = {
        pageSizeOptions: ['5', '10', '20', '50'], // 页条数下拉选择器的选项
        defaultPageSize: 5, // 默认的页大小
        showSizeChanger: true, // 显示页大小改变器
    };

    const items = [
        {
            key: '1',
            label: <span>概览</span>,
            children: (
                <Flex gap="middle" align="stretch">
                    {ProfileCard}
                    <Card styles={{ body: { flex: 'auto', padding: 0 } }} style={{ display: 'flex', flex: 'auto', alignSelf: 'stretch' }}>
                        <iframe title="车辆定位" src={mapUrl} width="100%" height="100%" />
                    </Card>
                </Flex>
            )
        },
        {
            key: '2',
            label: <span>实时视频</span>,
            children: <VehicleLiveViewer />
        },
        {
            key: '3',
            label: <span>事件</span>,
            children: (
                <Card styles={{ body: { paddingTop: 0, paddingBottom: 0 } }}>
                    <Button type="primary" style={{ position: 'absolute', right: 60, marginTop: '8px', zIndex: 10 }} onClick={allLogs}>查看全部日志</Button>
                    <Table className='table-list' rowKey="id" columns={AlarmEventColumns} dataSource={vehicleLogList} pagination={pagination} onChange={handleChange} scroll={{y:'calc(100vh - 710px)'}}/>
                </Card>
            )
        }
    ];

    return (
        <Flex gap="middle" vertical align="stretch">
            <Card style={{ marginTop: customStyles.margin }} styles={{ body: { padding: 0 } }}>
                <StyleProfileCover></StyleProfileCover>
                <StyleProfileContent style={{ padding: '22px' }}>
                    <BgPrimary style={{ ...sAvatarICON, ...sDisplayFlexCenter }}>
                        <SvgTruck style={{ color: customStyles.colorPrimary, width: '50px', height: '50px' }} fill="currentColor" />
                    </BgPrimary>

                    <Flex gap="middle" vertical align="flex-start">
                        <Title level={5} style={{ marginBottom: '-13px', color: customStyles.colorPrimary }}>
                            {vehicle.licensePlate || '---'}
                        </Title>
                        <div>
                            <Space wrap className="subtitle" size={15} style={{ marginRight: '20px', gap: '8px', marginBottom: '10px' }}>
                                <div>
                                    <MapPin size={14} /> {lastLocation || '---'}
                                </div>
                                <div>
                                    <Calendar size={14} /> {lastOnline ? moment(lastOnline).format('YYYY 年 MM 月 DD 日') : '---'}
                                </div>
                            </Space>
                        </div>
                    </Flex>
                </StyleProfileContent>
            </Card>
            <Tabs defaultActiveKey="1" items={items} onChange={onTabChange} style={{ height: 'calc(100vh - 450px)',overflow: 'auto' }}/>
        </Flex>
    );
};

export default VehicleProfile;
